import React, { createContext, useContext, useRef, useState, useCallback, useEffect } from "react";

const SoundContext = createContext(null);

export function SoundProvider({ children }) {
  const [muted, setMuted] = useState(false);
  const ctxRef = useRef(null);
  const mutedRef = useRef(false);
  const musicRef = useRef(null);

  useEffect(() => {
    mutedRef.current = muted;
    if (musicRef.current) musicRef.current.muted = muted;
  }, [muted]);

  const startMusic = useCallback(() => {
    if (typeof window === "undefined") return;
    if (!musicRef.current) {
      const a = new Audio("/audio/silly_cat.mp3");
      a.loop = true;
      a.volume = 0.45;
      musicRef.current = a;
    }
    musicRef.current.muted = mutedRef.current;
    const p = musicRef.current.play();
    if (p && p.catch) p.catch(() => {});
  }, []);

  const getCtx = useCallback(() => {
    if (typeof window === "undefined") return null;
    if (!ctxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) ctxRef.current = new AC();
    }
    if (ctxRef.current && ctxRef.current.state === "suspended") ctxRef.current.resume();
    return ctxRef.current;
  }, []);

  const tone = useCallback((ctx, freq, start, dur, type = "sine", gain = 0.14) => {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, ctx.currentTime + start);
    g.gain.setValueAtTime(0.0001, ctx.currentTime + start);
    g.gain.exponentialRampToValueAtTime(gain, ctx.currentTime + start + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + start + dur);
    osc.connect(g);
    g.connect(ctx.destination);
    osc.start(ctx.currentTime + start);
    osc.stop(ctx.currentTime + start + dur + 0.02);
  }, []);

  const play = useCallback((kind = "pop") => {
    if (mutedRef.current) return;
    const ctx = getCtx();
    if (!ctx) return;
    if (kind === "pop") {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = "triangle";
      osc.frequency.setValueAtTime(520, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(880, ctx.currentTime + 0.08);
      g.gain.setValueAtTime(0.0001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.16, ctx.currentTime + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.12);
      osc.connect(g); g.connect(ctx.destination);
      osc.start(); osc.stop(ctx.currentTime + 0.14);
    } else if (kind === "meow") {
      tone(ctx, 660, 0, 0.14, "sine", 0.12);
      tone(ctx, 520, 0.1, 0.18, "sine", 0.12);
    } else if (kind === "sparkle") {
      tone(ctx, 780, 0, 0.1, "sine", 0.09);
      tone(ctx, 1040, 0.07, 0.1, "sine", 0.09);
      tone(ctx, 1320, 0.14, 0.14, "sine", 0.09);
    } else if (kind === "boop") {
      tone(ctx, 380, 0, 0.1, "sine", 0.13);
    } else if (kind === "yay") {
      [523, 659, 784, 1046].forEach((f, i) => tone(ctx, f, i * 0.08, 0.16, "triangle", 0.11));
    }
  }, [getCtx, tone]);

  const toggleMuted = useCallback(() => {
    getCtx();
    setMuted((m) => !m);
  }, [getCtx]);

  return (
    <SoundContext.Provider value={{ muted, toggleMuted, play, startMusic }}>
      {children}
    </SoundContext.Provider>
  );
}

export function useSound() {
  const ctx = useContext(SoundContext);
  if (!ctx) return { muted: false, toggleMuted: () => {}, play: () => {}, startMusic: () => {} };
  return ctx;
}
